/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/member-ordering */
// Angular Imports
import { Location } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Dictionary } from '@ngrx/entity';
// lodash
import { difference as _difference, get as _get } from 'lodash';
import { take } from 'rxjs/operators';

// My Account Imports
import { Policy } from '@amfam/policy/models';
import { AnalyticsFacade } from '@amfam/shared/analytics';
import { PolicyChangeRequestType } from '@amfam/shared/models';
import { ImpersonateRolesService } from '@amfam/shared/utility/impersonation';
import { ValidationService } from '@amfam/ui-kit';

import { ChangeRequest, ChangeRequestPolicies, PolicyChangeEmitter } from '../../models';
import { PolicyTransmuteService } from '../../services/policy-transmute.service';

@Component({
  selector: 'ds-plchangerequest-policies',
  templateUrl: './change-request-policies.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ChangeRequestPoliciesComponent implements OnInit {
  @Input() policyList: Policy[];
  @Output() changeRequestPolicies = new EventEmitter<ChangeRequestPolicies>();
  @Output() valueChangeEvent = new EventEmitter();

  @Output() cancelEvent = new EventEmitter();

  public policyChangeRequestForm: UntypedFormGroup;
  public commentInputCtrl: AbstractControl;

  public selectedPolicies: Dictionary<Policy> = {};
  public enabledForm: boolean;

  constructor(
    private policyTranmuteService: PolicyTransmuteService,
    private location: Location,
    public roleService: ImpersonateRolesService,
    private formBuilder: UntypedFormBuilder,
    private analyticsFacade: AnalyticsFacade
  ) {}

  ngOnInit() {
    this.policyChangeRequestForm = this.buildNameGroupForm(this.formBuilder);
    this.commentInputCtrl = this.policyTranmuteService.initializeFormControls(
      this.policyChangeRequestForm
    )[0];
    this.policyChangeRequestForm = this.buildControls(
      this.policyChangeRequestForm,
      this.policyList
    );
    /**
     * @description If this is a single policy with single risk, hide the checkbox option
     */
    if (this.policyList.length === 1 && this.policyList[0].riskDescriptionList.length === 1) {
      this.updateSelectedPolicies({
        policyNumber: _get(this.policyList[0], 'policyNumber'),
        policyControlValue: true,
        riskDescription: _get(this.policyList[0], 'riskDescriptionList[0]')
      });
    }

    // emits when there is any changes in values by comparing with current value for validating the modal.

    this.policyChangeRequestForm.valueChanges.pipe(take(1)).subscribe(() => {
      this.valueChangeEvent.emit();
    });
  }

  /**
   * @author: Abhishek Singh
   * @param changeRequestForm : Takes a form group and adds the new controls to the form
   * @returns FormGroup : Return the form which has the new controls and the initialized values.
   */
  private buildNameGroupForm(formBuilder: UntypedFormBuilder): UntypedFormGroup {
    return formBuilder.group({
      commentInputCtrl: [
        '',
        Validators.compose([
          Validators.required,
          Validators.maxLength(850),
          ValidationService.textInputValidator
        ])
      ]
    });
  }

  /**
   * @author: Abhishek Singh
   * @param changeRequestForm : Takes a form group and adds the new controls to the form
   * @returns FormGroup : Return the form which has the new controls and the initialized values.
   */
  private buildControls(
    changeRequestForm: UntypedFormGroup,
    policyList: Policy[]
  ): UntypedFormGroup {
    for (const policy of policyList) {
      changeRequestForm.addControl(policy.policyNumber, new UntypedFormControl(''));
    }
    return changeRequestForm;
  }

  public updateSelectedPolicies(policyChangedObj: PolicyChangeEmitter) {
    if (_get(policyChangedObj, 'policyControlValue', false)) {
      let matchedPolicy: Policy = this.policyList.find(
        policy => policy.policyNumber === policyChangedObj.policyNumber
      );
      /**
       * @description An auto policy can have multiple risk. When each risk is selected/unselected we should be
       * updating the policy dictionary with appropriate risk description list.
       */
      if (this.selectedPolicies[policyChangedObj.policyNumber]) {
        matchedPolicy = Object.assign({}, matchedPolicy, {
          riskDescriptionList: [
            ...this.selectedPolicies[policyChangedObj.policyNumber].riskDescriptionList,
            policyChangedObj.riskDescription
          ]
        });
      } else {
        // If this is the first instance of risk selection
        matchedPolicy = Object.assign({}, matchedPolicy, {
          riskDescriptionList: [policyChangedObj.riskDescription]
        });
      }
      this.selectedPolicies[policyChangedObj.policyNumber] = matchedPolicy;
    } else if (this.selectedPolicies[policyChangedObj.policyNumber]) {
      /**
       * @description If the user unchecks the risk and it is the only risk currently added to the dictionary
       * remove the policy from the dictionary, else pop the risk description only
       */
      if (
        _get(
          this.selectedPolicies[policyChangedObj.policyNumber],
          'riskDescriptionList.length',
          0
        ) === 1
      ) {
        delete this.selectedPolicies[policyChangedObj.policyNumber];
      } else {
        const updatedRiskDescriptionList = _difference(
          _get(this.selectedPolicies[policyChangedObj.policyNumber], 'riskDescriptionList', []),
          [policyChangedObj.riskDescription]
        );
        this.selectedPolicies[policyChangedObj.policyNumber] = Object.assign(
          {},
          this.selectedPolicies[policyChangedObj.policyNumber],
          { riskDescriptionList: updatedRiskDescriptionList }
        );
      }
    }
    this.enabledForm = !!_get(Object.entries(this.selectedPolicies), 'length', 0);
  }

  /**
   * @author: Abhishek Singh
   * @param void
   * @returns void
   * @description On click of the submit button grab the required feilds from the change request form
   * build it match the changeRequest type and emit it out to the parent to do stuff with it. We don't
   * care what the parent does with it, its their problem.
   */
  public submit(): void {
    let changeRequest = new ChangeRequest();

    changeRequest = Object.assign({}, changeRequest, {
      policyChangeType: PolicyChangeRequestType.POLICY_COVERAGE_CHANGE,
      policyList: Object.values(this.selectedPolicies),
      comment: this.commentInputCtrl.value
    });

    this.analyticsFacade.trackEvent({
      event: 'change_profile_success',
      profile_change_type: 'policy'
    });

    // Emit the selected policies
    this.changeRequestPolicies.emit(changeRequest);
  }

  /**
   * @author: Abhishek Singh
   * @returns void
   * @description Self explanatory
   */
  public cancel(): void {
    this.cancelEvent.emit();
  }
}
